import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const STAGES = [1, 2, 3, 4];

const TextBlock = ({ entry, onChange, testId }) => (
  <div className="space-y-1.5">
    <input
      data-testid={`${testId}-heading`}
      value={entry?.heading || ''}
      onChange={(e) => onChange({ ...entry, heading: e.target.value })}
      placeholder="Heading shown above the text"
      className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-1.5 text-sm
                 text-gray-200 focus:outline-none focus:border-cyan-400 transition-colors"
    />
    <textarea
      data-testid={`${testId}-body`}
      value={entry?.body || ''}
      onChange={(e) => onChange({ ...entry, body: e.target.value })}
      rows={5}
      placeholder="What the participant reads at this stage"
      className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                 text-gray-200 leading-relaxed focus:outline-none focus:border-cyan-400
                 transition-colors"
    />
  </div>
);

export const InterpretationLibraryCard = () => {
  const [data, setData] = useState(null);
  const [dimensionTexts, setDimensionTexts] = useState({});
  const [stageTexts, setStageTexts] = useState({});
  const [activeDimension, setActiveDimension] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/assessments/interpretations`, { headers: headers() });
      setData(res.data);
      setDimensionTexts(res.data.library.dimension_texts || {});
      setStageTexts(res.data.library.stage_texts || {});
      setActiveDimension(res.data.dimensions[0]?.key || null);
    } catch {
      toast.error('Could not load the interpretation library');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const dirty =
    JSON.stringify(dimensionTexts) !== JSON.stringify(data.library.dimension_texts || {}) ||
    JSON.stringify(stageTexts) !== JSON.stringify(data.library.stage_texts || {});

  const stageName = (n) => data.stages.find((s) => s.stage === n)?.name || `Stage ${n}`;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/assessments/interpretations`,
        { dimension_texts: dimensionTexts, stage_texts: stageTexts },
        { headers: headers() },
      );
      setData((prev) => ({ ...prev, library: res.data.library }));
      toast.success('Interpretation library saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the interpretation library');
    } finally {
      setSaving(false);
    }
  };

  const setDimensionEntry = (dimension, stage, entry) =>
    setDimensionTexts((prev) => ({
      ...prev,
      [dimension]: { ...(prev[dimension] || {}), [String(stage)]: entry },
    }));

  return (
    <Card className="bg-gray-900/60 border-gray-700" data-testid="interpretation-library-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Interpretation Library
            </h3>
            <p className="text-sm text-gray-400 mt-1 max-w-2xl">
              What a participant reads about their result. Written once and used by all 13
              assessments — six dimensions across four stages, plus one for the overall stage.
            </p>
          </div>
          <Badge variant="outline" className="text-gray-300 border-gray-600 whitespace-nowrap"
                 data-testid="interpretation-field-count">
            {data.field_count} fields
          </Badge>
        </div>

        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="interpretation-dimension-tabs">
          {data.dimensions.map((d) => (
            <button
              key={d.key}
              onClick={() => setActiveDimension(d.key)}
              data-testid={`interpretation-dimension-tab-${d.key}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeDimension === d.key
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {d.name}
            </button>
          ))}
          <button
            onClick={() => setActiveDimension('__overall__')}
            data-testid="interpretation-dimension-tab-overall"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeDimension === '__overall__'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Overall stage
          </button>
        </div>

        <div className="mt-5 space-y-5">
          {activeDimension === '__overall__'
            ? STAGES.map((stage) => (
                <div key={stage}>
                  <h4 className="text-sm font-semibold text-gray-200 mb-2">
                    Stage {stage} — {stageName(stage)}
                  </h4>
                  <TextBlock
                    entry={stageTexts[String(stage)]}
                    onChange={(entry) =>
                      setStageTexts((prev) => ({ ...prev, [String(stage)]: entry }))}
                    testId={`interpretation-overall-${stage}`}
                  />
                </div>
              ))
            : STAGES.map((stage) => (
                <div key={stage}>
                  <h4 className="text-sm font-semibold text-gray-200 mb-2">
                    Stage {stage} — {stageName(stage)}
                  </h4>
                  <TextBlock
                    entry={(dimensionTexts[activeDimension] || {})[String(stage)]}
                    onChange={(entry) => setDimensionEntry(activeDimension, stage, entry)}
                    testId={`interpretation-${activeDimension}-${stage}`}
                  />
                </div>
              ))}
        </div>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="interpretation-save-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {saving ? 'Saving…' : 'Save interpretation text'}
          </Button>
          {dirty && (
            <button
              onClick={() => {
                setDimensionTexts(data.library.dimension_texts || {});
                setStageTexts(data.library.stage_texts || {});
              }}
              data-testid="interpretation-reset-btn"
              className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Discard changes
            </button>
          )}
          <span className="ml-auto text-xs text-gray-500">
            Every field must have text — a blank one would show a member an empty result.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default InterpretationLibraryCard;
